import api from './api';

export interface DeliveryFeeRule {
  id?: number;
  name: string;
  description: string;
  rule_type: string;
  priority: number;
  is_active: boolean;
  rule_config: Record<string, any>;
}

export interface DeliveryFeeCalculationRequest {
  order_total: number;
  order_date?: string; // ISO date string
  customer_address?: {
    address?: string;
    neighborhood?: string;
    city?: string;
    postal_code?: string;
  };
  has_promotion?: boolean;
}

export interface DeliveryFeeCalculationResult {
  delivery_fee: number;
  applied_rules: Array<{
    rule_id: number;
    rule_name: string;
    fee_amount: number;
  }>;
}

const deliveryFeeService = {
  /**
   * Calculate delivery fee based on configured rules
   */
  async calculateDeliveryFee(request: DeliveryFeeCalculationRequest): Promise<DeliveryFeeCalculationResult> {
    try {
      const response = await api.post('/delivery-fee/calculate', request) as DeliveryFeeCalculationResult;
      return response;
    } catch (error: any) {
      console.error('Error calculating delivery fee:', error);
      // Return default result if API fails
      const fallbackResult = {
        delivery_fee: 0,
        applied_rules: []
      };
      return fallbackResult;
    }
  },

  /**
   * Get all delivery fee rules
   */
  async getDeliveryFeeRules(): Promise<DeliveryFeeRule[]> {
    try {
      return await api.get('/delivery-fee/rules') as DeliveryFeeRule[];
    } catch (error: any) {
      console.error('Error fetching delivery fee rules:', error);
      return [];
    }
  },

  /**
   * Create a new delivery fee rule
   */
  async createDeliveryFeeRule(rule: Omit<DeliveryFeeRule, 'id'>): Promise<DeliveryFeeRule> {
    try {
      return await api.post('/delivery-fee/rules', rule) as DeliveryFeeRule;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to create delivery fee rule');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Update an existing delivery fee rule
   */
  async updateDeliveryFeeRule(ruleId: number, rule: Partial<DeliveryFeeRule>): Promise<DeliveryFeeRule> {
    try {
      return await api.put(`/delivery-fee/rules/${ruleId}`, rule) as DeliveryFeeRule;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to update delivery fee rule');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Delete a delivery fee rule
   */
  async deleteDeliveryFeeRule(ruleId: number): Promise<void> {
    try {
      await api.delete(`/delivery-fee/rules/${ruleId}`);
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to delete delivery fee rule');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  }
};

export default deliveryFeeService;
